import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  useRef,
} from 'react';

import {
  AriaDateRangePickerProps,
  DateValue,
  useDateRangePicker,
} from '@react-aria/datepicker';
import {
  DateRangePickerStateOptions,
  useDateRangePickerState,
} from './useDateRangePickerState';

import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/dateField/dateField.module.css';
import { Calendar } from '@danske/sapphire-icons/react';
import { DateRangeInput } from './DateRangeInput';
import { RangeCalendar, PredefinedDateRange } from '../../Calendar';
import { DateFieldButton } from './DateFieldButton';
import { PressResponder } from '@react-aria/interactions';
import { I18nProvider, useLocale } from '@react-aria/i18n';
import { useDialog } from '@react-aria/dialog';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { Field } from '../../Field';
import { Icon } from '../../Icon';
import { Label } from '../../Label';
import { View } from '../../View';
import { Popover } from '../../Popover';
import { tokens } from '@danske/sapphire-css/themes';
import {
  BaseComponentKeys,
  DateFieldBaseKeys,
  GlobalDomAttributes,
  OverlayTriggerPropsKeys,
  ValueBasePropsKeys,
} from '../../utils/types';

export type DateRangeFieldProps<T extends DateValue, P extends string> = Pick<
  AriaDateRangePickerProps<T>,
  | 'endName'
  | 'startName'
  | DateFieldBaseKeys
  | ValueBasePropsKeys
  | BaseComponentKeys
  | OverlayTriggerPropsKeys
> &
  SapphireStyleProps &
  GlobalDomAttributes & {
    /**
     * Handler that is called when the value changes.
     */
    onChange?: DateRangePickerStateOptions<DateValue, P>['onChange'];
    /**
     * Whether week numbers are visible as a first column
     */
    showWeekNumbers?: boolean;
    /**
     * Whether to show two months at the same time.
     */
    showTwoMonths?: boolean;
    /**
     * A set of predefined date ranges the user can more conveniently select.
     */
    predefinedRanges?: Record<P, PredefinedDateRange>;
    /**
     * Whether range is allowed to span unavailable dates.
     * @default false
     */
    allowUnavailableDatesWithinRange?: boolean;
    /**
     * Whether the input should render as having an error and an error message.
     * This also sets the appropriate `aria` attributes on the input.
     *
     * A `boolean` sets the error state. Can be paired with the "note" prop with Field.Note variant="error"
     * A `ReactNode` sets the error state with an additional error message.
     *
     * **An error message is strongly recommended because an error should
     * always have an explanation about how to fix it.**
     */
    error?: boolean | ReactNode;
    /**
     * A note to show below the input.
     * If the input has an error message, this note will be replaced by that.
     * Use {@Link Field.Note} to render the note.
     */
    note?: ReactNode;
    /**
     * A HelpButton to render next to the label.
     */
    contextualHelp?: ReactNode;
    /**
     * If it should hide the "clear" button when input is not empty.
     *
     * @default false
     */
    noClearButton?: boolean;
    /**
     * If it should hide the "calendar" button
     *
     * @default false
     */
    noCalendarButton?: boolean;
    /**
     * Locale (eg. "da-DK", "en-US", "sv-SE" etc.)
     *
     * THIS IS A TEMPORARY SOLUTION FOR THE INTERFACE OF LOCALIZATION. THIS API
     * MAY CHANGE OR EVEN BE REPLACED WITH SOMETHING DIFFERENT IN A FUTURE VERSION.
     */
    locale?: string;
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
    /**
     * To visually indicate if this field is required or optional.
     * @default false
     */
    necessityIndicator?: boolean;

    /**
     * Places the label either above (default) or on the side of the control.
     * @default 'above'
     */
    labelPlacement?: 'side' | 'above';
  };

function _DateRangeField<T extends DateValue, P extends string>(
  {
    error,
    note,
    contextualHelp,
    noClearButton = false,
    locale,
    showWeekNumbers,
    showTwoMonths,
    allowUnavailableDatesWithinRange,
    predefinedRanges,
    size = 'lg',
    labelPlacement = 'above',
    necessityIndicator = false,
    noCalendarButton = false,
    ...otherProps
  }: DateRangeFieldProps<T, P>,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const groupRef = useRef(null);
  const dialogRef = useRef(null);

  const { locale: existingLocale } = useLocale();

  const preferredLocale = locale || existingLocale;

  const { styleProps } = useSapphireStyleProps(otherProps);

  const { dialogProps } = useDialog({}, dialogRef);

  const state = useDateRangePickerState({
    ...otherProps,
    ...(error ? { isInvalid: true } : {}),
    ...(allowUnavailableDatesWithinRange
      ? { allowsNonContiguousRanges: allowUnavailableDatesWithinRange }
      : {}),
  });

  const {
    groupProps,
    labelProps,
    startFieldProps,
    endFieldProps,
    buttonProps,
    dialogProps: dialogPropsFromDatePicker,
    calendarProps,
    descriptionProps,
    errorMessageProps,
  } = useDateRangePicker(
    {
      ...otherProps,
      description: note,
      allowsNonContiguousRanges: allowUnavailableDatesWithinRange,
    },
    state,
    groupRef
  );

  const isMedium = size === 'md';

  return (
    <Field
      {...otherProps}
      noShrink
      isDisabled={otherProps.isDisabled}
      size={size}
      ref={ref}
      labelPlacement={labelPlacement}
      noDefaultWidth
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        <I18nProvider locale={preferredLocale}>
          {otherProps.label && (
            <Field.Label>
              <Label
                {...labelProps}
                size={size}
                necessityIndicator={
                  otherProps.isRequired && necessityIndicator
                    ? 'required'
                    : !otherProps.isRequired && necessityIndicator
                    ? 'optional'
                    : undefined
                }
                contextualHelp={contextualHelp}
              >
                {otherProps.label}
              </Label>
            </Field.Label>
          )}
          <Field.Control>
            <div
              {...groupProps}
              ref={groupRef}
              className={clsx(
                styles['sapphire-date-field'],
                styles['sapphire-date-field--range'],
                {
                  [styles['sapphire-date-field--error']]: state.isInvalid,
                  [styles['sapphire-date-field--no-calendar']]:
                    noCalendarButton,
                  [styles['sapphire-date-field--no-clear-button']]:
                    noClearButton,
                  [styles['sapphire-date-field--md']]: isMedium,
                }
              )}
              {...(styleProps.style.width
                ? { style: { width: styleProps.style.width } }
                : {})}
            >
              {!noCalendarButton && (
                <PressResponder {...buttonProps} isPressed={state.isOpen}>
                  <DateFieldButton
                    isDisabled={otherProps.isDisabled || otherProps.isReadOnly}
                  >
                    <Icon size={isMedium ? 'sm' : 'md'}>
                      <Calendar />
                    </Icon>
                  </DateFieldButton>
                </PressResponder>
              )}
              <DateRangeInput
                dateRangePickerState={state}
                startFieldProps={startFieldProps}
                endFieldProps={endFieldProps}
                noClearButton={noClearButton}
                triggerButtonId={buttonProps.id}
                size={size}
              />
            </div>
          </Field.Control>
          {((!!error && typeof error !== 'boolean') || note) && (
            <Field.Footer>
              {!!error && typeof error !== 'boolean' ? (
                <Field.Note variant="error">{error}</Field.Note>
              ) : note ? (
                note
              ) : (
                <></>
              )}
            </Field.Footer>
          )}
          {state.isOpen && (
            <Popover
              state={state}
              triggerRef={groupRef}
              placement="bottom start"
            >
              <div
                ref={dialogRef}
                {...dialogProps}
                {...dialogPropsFromDatePicker}
              >
                <View padding={tokens.size.spacingLg}>
                  <RangeCalendar
                    {...calendarProps}
                    size="md"
                    allowUnavailableDatesWithinRange={
                      allowUnavailableDatesWithinRange
                    }
                    showWeekNumbers={showWeekNumbers}
                    showTwoMonths={showTwoMonths}
                    predefinedRanges={predefinedRanges}
                    showFixedWeeks
                  />
                </View>
              </div>
            </Popover>
          )}
        </I18nProvider>
      </Field.Context>
    </Field>
  );
}

/**
 * A date range field allows users to select a range of dates by selecting a to and a from date with keyboard and/or mouse.
 */
export const DateRangeField = forwardRef(_DateRangeField) as <
  T extends DateValue,
  P extends string
>(
  props: DateRangeFieldProps<T, P> & { ref?: RefObject<HTMLDivElement> }
) => React.ReactElement;
